import * as React from 'react';
import { claimVoipMasterIfVacant, isVoipEnabled, userSession } from 'state/session';
import { myCameraStream } from 'state/video';
import { user } from 'utils/auth/user';
import logger from 'utils/log';
import { tenantConfig } from 'utils/tenant';
import { voipConnectionEpoch, voipRtcConnection } from 'utils/ui/rtc';
import { voipMasterTabId } from 'state/session/voipClaim';
import { isVoipRenderSinkActive, reportVoipRenderList } from 'utils/ui/voipRenderSink';
import {
  dealerPseudonymOf,
  dedupeRemotesByClient,
  dedupeRemotesByDealer,
  describeRenderList,
  filterDeadTransportTiles,
  filterIdentityPendingTiles,
  resolveActiveConnectionId,
} from 'utils/ui/voipRemotes';
import type { TRTCConnection } from 'webrtc/types';

/**
 * Active-speaker ranking. Centralised so consumers (component layer, the hook
 * itself, and any future ranking-aware feature) share a single source of truth.
 * Returns 0 for speakers that should sort first, 1 otherwise.
 */
const speakerRank = (remote: TRTCConnection): number => (remote.isActiveSpeaker || remote.lastActiveSpeaker ? 0 : 1);

const useVoipConnections = () => {
  userSession().useState();
  tenantConfig().useState();
  myCameraStream().useState();
  // AVS-4302: re-render (and re-read the module binding below) whenever the connection is
  // rebuilt — a takeover/step-down swaps `voipRtcConnection` for a fresh RTCClient while the
  // VoipPanel stays mounted, so without this trigger the `store.useState()` call keeps its
  // subscription to the OLD store and the stepped-down master never sees the new master's
  // loopback. Reading the epoch first guarantees the line below resolves the CURRENT instance.
  voipConnectionEpoch.useState();

  const { remoteConnections: rawRemoteConnections } = voipRtcConnection?.store?.useState() || {
    remoteConnections: {},
  };

  // AVS-4302 (§5.5, CG-6): a standby dealer has no local camera; its own face arrives as
  // a REMOTE whose meta.dealerPseudonym matches this dealer (the master's mirror). That
  // loopback must NOT render as a separate participant tile, must NOT become the active
  // speaker, and must NOT be picked up by SingularVoip / onActiveParticipantChange — so
  // exclude it at the SOURCE (not just from sortedRemotes). It is surfaced separately as
  // `selfMirrorRemote` for the "You" tile. Identity-scoped + standby-gated so a flag-off
  // tab, a master, or a different dealer never filters anything (today's behaviour).
  const isStandby = userSession().isVoipStandby;
  const myPseudonym = user.userPseudonym;
  // AVS-4302: with 2+ standby tabs of the same dealer there are SEVERAL same-dealer remotes
  // (the publishing master PLUS the other receive-only standbys). The self-mirror must be the
  // one that actually carries the dealer's face — the PUBLISHING master, i.e. the remote with a
  // live stream — not just the first by arrival order. Picking first-by-order is non-deterministic
  // and, after a takeover or with 3+ tabs, can latch onto a streamless sibling standby, leaving the
  // "You" tile on a placeholder while the master's loopback is actually flowing. Prefer a
  // live-stream same-dealer remote; fall back to the first only until one starts streaming.
  // AVS-4392: run the same dead-transport rule the participant list uses (one owner:
  // `filterDeadTransportTiles`) BEFORE the pick — `stream instanceof MediaStream` below stays true
  // for a written-off master's stale, frozen stream object, so without this the "You" tile latches
  // onto the very gray rectangle B3 removes from the participant list.
  const sameDealerRemotes = myPseudonym
    ? filterDeadTransportTiles(Object.values(rawRemoteConnections)).filter(
        (remote) => dealerPseudonymOf(remote) === myPseudonym
      )
    : [];
  const selfMirrorConnectionId = isStandby
    ? (sameDealerRemotes.find((remote) => remote.stream instanceof MediaStream) ?? sameDealerRemotes[0])?.connectionId
    : undefined;
  // Memoize so the standby's "You" tile keeps a stable ref across WebRTC store ticks —
  // otherwise the inner <Video> remounts on every tick (audio glitch risk).
  const selfMirrorRemote = React.useMemo(
    () => (selfMirrorConnectionId ? rawRemoteConnections[selfMirrorConnectionId] : undefined),
    [rawRemoteConnections, selfMirrorConnectionId]
  );

  // The single-stream flag gates BOTH the dedup (§5.6) and the empty-tile filter (§5.8).
  // It already requires remote + voip + voipSingleStream, so flag-off ⇒ the list is
  // untouched (exactly today's behaviour — the minimal-regression guarantee, and the
  // CG-4 design: a flag-ON viewer dedups even a stale/flag-off duplicate publisher).
  const isSingleStream = userSession().isVoipSingleStreamEnabled;
  // PO decision (AVS-4302): a suppressed standby shows ONLY its own Takeover tile — no
  // participant tiles at all. Render-only, like dedup: the gate below still keys on
  // rawPublishableCount, so the receive-only handshake and the master's loopback are untouched.
  const suppressIncoming = userSession().isVoipIncomingMediaSuppressed;

  // AVS-4302 Task 10 / AVS-4363: the render source, derived AFTER the Task 8 self-mirror
  // exclusion. When the flag is on we (1) drop the standby's own loopback (above), then
  // (2) dedup remotes by dealerPseudonym so the customer never sees one dealer twice.
  // Dedup's tie-break keeps the active speaker, so the active-speaker resolution still
  // operates on a sane list. There is deliberately NO stream-based exclusion (AVS-4363:
  // presence = tile — a camera-denied dealer publishes nothing and must still get a tile).
  //
  // `rawPublishableCount` is the RAW peer count (self-mirror excluded, BUT before dedup).
  // The publish/offer trigger keys on THIS — not the render count — because dedup affects
  // RENDERING only. A customer whose only peer is a same-dealer duplicate must STILL send
  // its own publish() offer.
  const { remoteConnections, rawPublishableCount } = React.useMemo(() => {
    const withoutSelfMirror = selfMirrorConnectionId
      ? Object.fromEntries(Object.entries(rawRemoteConnections).filter(([id]) => id !== selfMirrorConnectionId))
      : rawRemoteConnections;
    const rawCount = Object.keys(withoutSelfMirror).length;
    if (suppressIncoming) {
      return { remoteConnections: {}, rawPublishableCount: rawCount };
    }
    // AVS-4302 takeover reliability: offer-admitted peers awaiting identity (metaPending) are
    // negotiable but NOT renderable — without meta the filters below read them as customers
    // (phantom tile). Render-only and unconditional (both flag states); rawCount above keeps
    // them, so the publish/negotiate gate is unaffected.
    // AVS-4392 B3: also drop peers the transport-recovery ladder abandoned. Unconditional (both
    // flag states) and applied AFTER `rawCount` above, exactly like the identity-pending filter —
    // a dead tile is wrong in every configuration, and the publish/negotiate gate must not shift.
    // AVS-4425: collapse a rejoining tab's old and new rows to ONE tile (`meta.clientIdHash`).
    // Unconditional, like the two filters it follows and for the same reason — a duplicate of the
    // same human is wrong in every configuration — and applied AFTER `rawCount` so the
    // publish/negotiate gate does not shift. This is the only such rule that protects CUSTOMERS:
    // the same-dealer dedup below keys on a pseudonym they do not have.
    const identified = Object.fromEntries(
      dedupeRemotesByClient(filterDeadTransportTiles(filterIdentityPendingTiles(Object.values(withoutSelfMirror)))).map(
        (remote) => [remote.connectionId, remote]
      )
    );
    if (!isSingleStream) {
      return { remoteConnections: identified, rawPublishableCount: rawCount };
    }
    // AVS-4302: a dealer's OWN other tabs (same pseudonym) are never participant tiles. A
    // standby's own tab is the self-mirror — already excluded above and shown in the self tile;
    // a master's same-dealer remotes are its stepping-down predecessor or receive-only standby
    // siblings. During a takeover the OLD master's still-live stream would otherwise briefly
    // render as a second participant tile next to the new master's own camera (the reported
    // takeover "double video"). Drop ALL same-dealer remotes from the render list so a dealer
    // never sees itself as a participant — the exclusion is by IDENTITY, so it holds whether or
    // not the own tab is publishing. Customers (no pseudonym) are unaffected — `myPseudonym` is
    // empty and never matches a dealer remote — so customer-side dedup of DISTINCT dealers is
    // unchanged. The publish/negotiate gate is unaffected: it keys on `rawPublishableCount`
    // (computed above, before this filter), so the new master still publishes to the stepping-down
    // tab and the old master still gets the new loopback.
    const renderableRemotes = myPseudonym
      ? Object.values(identified).filter((remote) => dealerPseudonymOf(remote) !== myPseudonym)
      : Object.values(identified);
    // AVS-4363: dedup only. A remote that is connected but publishes nothing (camera+mic denied
    // ⇒ receive-only) keeps its tile and renders a <VideoPlaceholder /> — it used to be dropped
    // by `filterEmptyDealerTiles`, which made a camera-less dealer invisible to the customer.
    const display = dedupeRemotesByDealer(renderableRemotes);
    return {
      remoteConnections: Object.fromEntries(display.map((conn) => [conn.connectionId, conn])),
      rawPublishableCount: rawCount,
    };
  }, [rawRemoteConnections, selfMirrorConnectionId, isSingleStream, suppressIncoming, myPseudonym]);

  const connections = Object.values(remoteConnections);
  const [activeConnectionId, setActiveConnectionId] = React.useState<string>('');

  // AVS-4302: depend on this tab's master status, not just connection count. A tab that
  // becomes master AFTER connections settle (fallback-claim timing, or a standby promoted
  // to master) must re-evaluate the publish gate — otherwise it never publishes and the
  // customer is left without a dealer stream.
  const myVoipMasterTabId = voipMasterTabId(userSession().get('voipMasters')?.[user.userPseudonym]);

  // AVS-4302: the standby's self-mirror (the master) is EXCLUDED from rawPublishableCount, but
  // it is precisely the peer the standby must run its receive-only handshake with to receive the
  // loopback. So a standby whose ONLY peer is the master — the customer has left (but
  // customerAllowed keeps VOIP active) or it connected before any customer — had a gate count of
  // 0 and NEVER negotiated: the master↔standby pair stayed at signalingState 'stable' and the
  // "You" tile showed a placeholder forever (the reported bug; confirmed on pr1623 with a stuck
  // pc at conn/ice 'new', sig 'stable'). Count the self-mirror back in for the gate. Publishing
  // is unaffected — a publisher is never a standby, so its self-mirror id is always undefined and
  // this equals rawPublishableCount (a publisher still needs a real, non-self peer).
  const peerCountForGate = rawPublishableCount + (selfMirrorConnectionId ? 1 : 0);

  React.useEffect(() => {
    // Gate on the RAW peer count, NOT the deduped/filtered render count (§5.6/§5.8 affect
    // rendering only). A customer whose sole peer is a not-yet-streaming dealer is dropped
    // from the render list but must still publish its own offer. The standby branch additionally
    // counts its self-mirror master (see peerCountForGate) so it negotiates even as its sole peer.
    if (peerCountForGate <= 0 || !voipRtcConnection) return;

    // A dealer that reaches the gate with a vacant master slot claims it (no-op when the
    // flag is off, when not a remote dealer, or when already claimed — `claimVoipMasterIfVacant`
    // early-returns on a taken slot). Then it EITHER publishes (master/customer) OR negotiates
    // receive-only (standby). The decision lives in state (`shouldPublishVoip`); the hook stays
    // a thin trigger. Each pass fires at most once per RTCClient instance — the memory lives on
    // the instance (`lastNegotiationPass`), not in hook refs, so a remount against a still-live
    // connection (DCC re-creates `<e3d-stream>`) cannot re-offer to every peer, while
    // `destroy()` resets it so a genuine rebuild re-runs its pass.
    claimVoipMasterIfVacant();
    if (userSession().shouldPublishVoip) {
      // Publish unless this instance's last pass was already a publish (a standby promoted to
      // master must share its media).
      if (voipRtcConnection.lastNegotiationPass !== 'publish') {
        logger.log('[VOIP] SingularVOIP - Publishing user stream...');
        voipRtcConnection.publish();
      }
    } else if (voipRtcConnection.lastNegotiationPass !== 'receive-only') {
      // Standby: no media, but it must negotiate (recvonly) so the master's loopback reaches
      // it. Without this, a peer pair where the standby owns the higher connectionId never
      // negotiates and the standby shows a placeholder until an unrelated mute/unmute on the
      // master forces an out-of-band offer. A master demoted in place also lands here — its
      // last pass was 'publish' — so the senders are torn down and incoming-media suppression
      // re-engages.
      logger.log('[VOIP] SingularVOIP - Standby negotiating receive-only...');
      voipRtcConnection.negotiateAsReceiver();
    }
    // peerCountForGate already subsumes rawPublishableCount (it is that plus the self-mirror),
    // so it is the only count dep needed.
  }, [peerCountForGate, myVoipMasterTabId]);

  // AVS-4311: promote the last-known speaker to the single pill/active slot deterministically.
  // The selection rules live in `resolveActiveConnectionId` (utils, unit-tested) so this hook
  // stays a thin trigger. This also FIXES the old inline pick, which: fell back to the arbitrary
  // `connections[0]` (the "shows 1 person randomly" bug), clung to a departed `prev` (stale tile),
  // and via `find(isActiveSpeaker || lastActiveSpeaker)` could promote a stale last-speaker sitting
  // ahead of the live speaker (the helper now prefers `isActiveSpeaker` over `lastActiveSpeaker`).
  React.useEffect(() => {
    setActiveConnectionId((prev) => resolveActiveConnectionId(connections, prev));
  }, [connections]);

  // Active/last-active speakers first, others after. Derived here so the
  // component layer stays presentational and ranking rules have one home.
  const sortedRemotes = React.useMemo<TRTCConnection[]>(
    () => (remoteConnections ? Object.values(remoteConnections).sort((a, b) => speakerRank(a) - speakerRank(b)) : []),
    [remoteConnections]
  );

  // `true` when any remote is currently the active speaker — used by the pill
  // to pulse without re-mounting this hook in the collapsed view.
  const isAnyActiveSpeaker = React.useMemo(
    () => sortedRemotes.some((remote) => Boolean(remote.isActiveSpeaker)),
    [sortedRemotes]
  );

  // Phone (≤629px) visibility fallback: exactly ONE tile is shown. Priority:
  // active-speaker remote → first remote with camera on → self if camera on → none.
  // Consumed by the `ex3d-video--phone-hidden` class on each tile.
  const isSelfVideoEnabled = !!myCameraStream().get('isVideoEnabled');
  const phoneDisplayedKey = React.useMemo<string | null>(() => {
    const activeRemote = sortedRemotes.find((r) => r.isActiveSpeaker);
    if (activeRemote) return `remote:${activeRemote.connectionId}`;
    const cameraOnRemote = sortedRemotes.find((r) => !r.videoMuted && r.stream instanceof MediaStream);
    if (cameraOnRemote) return `remote:${cameraOnRemote.connectionId}`;
    if (isSelfVideoEnabled) return 'self';
    return null;
  }, [sortedRemotes, isSelfVideoEnabled]);

  // AVS-4423 Task 1.8 (fix round 1): which remotes the UI actually decided to render, and why the
  // others were hidden — the evidence gap the 2026-09-04 multi-party incident review needed most.
  // `describeRenderList` reuses the SAME predicates this hook composes above, in the SAME order,
  // including the two identities (`myPseudonym`, `selfMirrorConnectionId`) and the suppression flag
  // this module needs to derive `'self-mirror'`/`'suppressed'` correctly rather than guess at them.
  //
  // A `useEffect`, not the memo above: `React.useMemo` is contractually a pure calculation that
  // React may compute and discard for a render that never commits (and StrictMode double-invokes in
  // dev) — an effect keyed on the same real inputs reports once per COMMITTED change instead, with
  // the tap's own fingerprint dedup (`rtcTap.ts`) as the second line of defence against a re-run
  // that changes nothing.
  //
  // This reports the hook's OWN render list — it has no `remoteSlots` (that is a `MultiVoipInternal`
  // prop, `maxParticipants`, this hook is never given) and so cannot know about the tile cap that
  // component applies on top; `MultiVoipInternal.tsx` and `SingularVoip.tsx` each report that
  // separately, observing their own on-screen selection. `source: 'hook'` (fix round 2) is what lets
  // a reader — and `rtcTap.ts`'s own de-duplication — tell the three emitters apart; without it, an
  // uncapped/unselected report from either component (`hidden: []`) is byte-identical to "the hook
  // hid nothing" and silently overwrites this event for a reader taking the latest as current.
  React.useEffect(() => {
    // Final review C3: `describeRenderList` runs several filter/dedupe passes over the remote list
    // for a result nobody reads once the sink is the no-op — the recorder ships on dev/prelive
    // only, so this is `false` for every production tab. Checked FIRST so prod pays one boolean.
    if (!isVoipRenderSinkActive()) {
      return;
    }

    reportVoipRenderList({
      source: 'hook',
      ...describeRenderList({
        raw: Object.values(rawRemoteConnections),
        rendered: sortedRemotes,
        myPseudonym,
        selfMirrorConnectionId,
        suppressIncoming,
      }),
    });
  }, [rawRemoteConnections, sortedRemotes, myPseudonym, selfMirrorConnectionId, suppressIncoming]);

  return {
    remoteConnections,
    activeConnectionId,
    sortedRemotes,
    isAnyActiveSpeaker,
    phoneDisplayedKey,
    // AVS-4302 (§5.5): the standby dealer's own loopback, rendered in the "You" tile
    // instead of `myCameraStream` (which a standby never has). `undefined` for a master,
    // a customer, a flag-off tab, or before the loopback arrives — the self tile then
    // falls back to `myCameraStream` exactly as today.
    selfMirrorRemote,
    canShowVoip: tenantConfig().isAllowed('voip') && isVoipEnabled(),
    /**
     * The exact count the publish/negotiate effect gates on. Exposed so the "render rules must not
     * shift the gate" guarantee is ASSERTABLE: the gate is `peerCountForGate <= 0 -> return`, and
     * every render rule above (dedup by tab, dedup by dealer, the dead-transport and
     * identity-pending filters) leaves at least one remote standing, so watching `publish()` alone
     * can never detect a rule that moved ahead of the raw count. Nothing renders from this.
     */
    peerCountForGate,
  };
};

export default useVoipConnections;
